/**
 * SSH Bastions — admin tool to manage shared SSH bastion hosts.
 *
 * Bastions are first-class shared objects: define one (host/user/key), attach
 * many connections to it. Private keys never sit on disk — they're stored in
 * the same AES-GCM vault that protects MySQL passwords. The connection form's
 * "Through bastion" dropdown lists these; the MySQL pool dials through the
 * matching SSH client transparently.
 *
 * Honest scope: TOFU host-key verification — first successful Test records the
 * remote host key fingerprint; later connects refuse if it changes.
 */
import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { toast } from '../lib/toast.js';
import {
  listBastions, createBastion, updateBastion, deleteBastion, testBastion,
} from '../lib/sshbastions.js';

const blankInput = () => ({
  name: '', host: '', port: 22, user: '',
  authMethod: 'key', secret: '', passphrase: '',
});

export default function SshBastions(props) {
  const [list, { refetch }] = createResource(listBastions);
  const [editing, setEditing] = createSignal(null); // null | { id?: number, input: Input, isNew: bool }
  const [busyId, setBusyId] = createSignal(0);

  const openNew = () => setEditing({ input: blankInput(), isNew: true });
  const openEdit = (b) => setEditing({
    id: b.id, isNew: false,
    // Don't pre-fill the existing secret — fields stay empty and the server
    // treats empty (= nil) as "leave unchanged on update".
    input: { name: b.name, host: b.host, port: b.port, user: b.user,
             authMethod: b.authMethod, secret: '', passphrase: '' },
  });

  const save = async () => {
    const e = editing();
    if (!e) return;
    const i = e.input;
    // For "edit" mode an empty secret means "don't change" — send null. For
    // "new" with auth=agent we may omit secret entirely.
    const payload = {
      name: i.name.trim(), host: i.host.trim(), port: Number(i.port) || 22,
      user: i.user.trim(), authMethod: i.authMethod,
      secret: i.secret ? i.secret : (e.isNew ? '' : null),
      passphrase: i.passphrase ? i.passphrase : (e.isNew ? '' : null),
    };
    try {
      if (e.isNew) await createBastion(payload);
      else await updateBastion(e.id, payload);
      toast.success('Bastion saved.');
      setEditing(null);
      refetch();
    } catch (err) { toast.error('Save failed: ' + err.message); }
  };

  const remove = async (b) => {
    if (!confirm(`Delete bastion "${b.name}"? Connections referencing it will fail to dial until detached.`)) return;
    setBusyId(b.id);
    try { await deleteBastion(b.id); toast.success('Bastion deleted.'); refetch(); }
    catch (err) { toast.error('Delete failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const test = async (b) => {
    setBusyId(b.id);
    try {
      const r = await testBastion(b.id);
      if (r.ok) {
        toast.success(`Connected to "${b.name}"${r.bastion?.hostFingerprint ? '\nHost key: ' + r.bastion.hostFingerprint : ''}`);
        refetch();
      } else {
        toast.error('Test failed: ' + (r.error || 'unknown error'));
      }
    } catch (err) { toast.error('Test failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const authLabel = (m) => m === 'password' ? 'Password' : m === 'key' ? 'Private key' : 'SSH agent';

  return (
    <Modal open={true} onClose={props.onClose} title="SSH Bastions" wide>
      <p class="muted" style="font-size:12px;margin:0 0 12px;">
        Define a bastion once; attach many connections to it (Connection editor → <em>Through bastion</em>).
        Keys are encrypted at rest with the master key.
      </p>

      <div style="display:flex;justify-content:flex-end;margin:0 0 10px;">
        <button class="btn" onClick={openNew}>+ New bastion</button>
      </div>

      <Show when={!list.loading} fallback={<div class="muted">Loading…</div>}>
        <Show when={(list() || []).length === 0}>
          <div class="muted" style="font-size:12px;">No bastions yet — create one to tunnel a connection through it.</div>
        </Show>
        <table class="settings-backups mono" style="width:100%;">
          <thead><tr>
            <th>Name</th><th>Host</th><th>User</th><th>Auth</th><th>Fingerprint</th><th></th>
          </tr></thead>
          <tbody>
            <For each={list() || []}>
              {(b) => (
                <tr>
                  <td>{b.name}</td>
                  <td>{b.host}:{b.port}</td>
                  <td>{b.user}</td>
                  <td>{authLabel(b.authMethod)}{b.hasSecret ? '' : <span class="muted"> · no secret</span>}</td>
                  <td title={b.hostFingerprint || 'not verified yet — Test to record (TOFU)'}>
                    {b.hostFingerprint ? b.hostFingerprint.slice(0, 22) + '…' : <span class="muted">not verified</span>}
                  </td>
                  <td style="text-align:right;white-space:nowrap;">
                    <button class="btn btn-sm" disabled={busyId() === b.id} onClick={() => test(b)}>Test</button>
                    <button class="btn btn-sm btn-ghost" onClick={() => openEdit(b)}>Edit</button>
                    <button class="btn btn-sm btn-danger" disabled={busyId() === b.id} onClick={() => remove(b)}>Delete</button>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>

      <Show when={editing()}>
        <Modal open={true} onClose={() => setEditing(null)} title={editing().isNew ? 'New SSH bastion' : `Edit bastion`}>
          <div class="settings-row"><label>Name</label>
            <input class="input" value={editing().input.name}
                   onInput={(e) => setEditing({ ...editing(), input: { ...editing().input, name: e.target.value } })} /></div>
          <div class="settings-row"><label>Host</label>
            <input class="input" value={editing().input.host}
                   onInput={(e) => setEditing({ ...editing(), input: { ...editing().input, host: e.target.value } })} /></div>
          <div class="settings-row"><label>Port</label>
            <input class="input settings-num" type="number" min="1" max="65535" value={editing().input.port}
                   onInput={(e) => setEditing({ ...editing(), input: { ...editing().input, port: e.target.value } })} /></div>
          <div class="settings-row"><label>SSH user</label>
            <input class="input" value={editing().input.user}
                   onInput={(e) => setEditing({ ...editing(), input: { ...editing().input, user: e.target.value } })} /></div>
          <div class="settings-row"><label>Auth</label>
            <select class="input" value={editing().input.authMethod}
                    onChange={(e) => setEditing({ ...editing(), input: { ...editing().input, authMethod: e.target.value } })}>
              <option value="key">Private key</option>
              <option value="password">Password</option>
              <option value="agent">SSH agent (server-side)</option>
            </select></div>

          <Show when={editing().input.authMethod === 'password'}>
            <div class="settings-row"><label>Password</label>
              <input class="input" type="password" placeholder={editing().isNew ? '' : 'Leave blank to keep current'}
                     value={editing().input.secret}
                     onInput={(e) => setEditing({ ...editing(), input: { ...editing().input, secret: e.target.value } })} /></div>
          </Show>

          <Show when={editing().input.authMethod === 'key'}>
            <div class="settings-row" style="align-items:flex-start;"><label>Private key (PEM)</label>
              <textarea class="input" rows="6" style="font-family:var(--font-mono);font-size:11.5px;"
                        placeholder={editing().isNew ? '-----BEGIN OPENSSH PRIVATE KEY-----\n…' : 'Leave blank to keep current'}
                        value={editing().input.secret}
                        onInput={(e) => setEditing({ ...editing(), input: { ...editing().input, secret: e.target.value } })}></textarea></div>
            <div class="settings-row"><label>Passphrase</label>
              <input class="input" type="password" placeholder="Only for encrypted private keys"
                     value={editing().input.passphrase}
                     onInput={(e) => setEditing({ ...editing(), input: { ...editing().input, passphrase: e.target.value } })} /></div>
          </Show>

          <Show when={editing().input.authMethod === 'agent'}>
            <p class="muted" style="font-size:12px;">Uses the SSH agent on the Ninocat server (<code class="mono">$SSH_AUTH_SOCK</code>).
              Useful when the server already has SSH access set up out-of-band.</p>
          </Show>

          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
            <button class="btn" onClick={save}>{editing().isNew ? 'Create' : 'Save'}</button>
          </div>
        </Modal>
      </Show>
    </Modal>
  );
}
